import { Controller } from "@hotwired/stimulus"
import { savePlan, isSectionIncluded } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Section header chip cluster — Rails-native port of the
// Include / Proofread / Preview pills at pcp_builder.html#5414-5432.
// Each chip reflects + mutates plan.meta.{sectionEnabled,proofread}
// through localStorage so the rest of the editor (preview, wizard,
// .sdp save) sees the change immediately.
const STORAGE_KEY = "pcp_plan_data"

export default class extends Controller {
  static targets = [
    "includeBtn", "includeLabel",
    "proofreadCheck", "proofreadIcon", "proofreadLabel"
  ]
  static values  = { key: String }

  connect() {
    this._refresh()
    this._onPlanChange = () => this._refresh()
    document.addEventListener("plan-store:change", this._onPlanChange)
  }
  disconnect() {
    document.removeEventListener("plan-store:change", this._onPlanChange)
  }

  toggleIncluded() {
    const plan = this._readPlan()
    if (!plan.meta) plan.meta = {}
    if (!plan.meta.sectionEnabled) plan.meta.sectionEnabled = {}
    plan.meta.sectionEnabled[this.keyValue] = !this._isIncluded(plan)
    this._writePlan(plan)
    this._refresh()
  }

  toggleProofread(event) {
    const plan = this._readPlan()
    if (!plan.meta) plan.meta = {}
    if (!plan.meta.proofread) plan.meta.proofread = {}
    const next = event ? !!event.target.checked : !plan.meta.proofread[this.keyValue]
    plan.meta.proofread[this.keyValue] = next
    this._writePlan(plan)
    this._refresh()
  }

  openPreview(event) {
    // Anchor click already provides /editor/native/preview#section-<key>
    // navigation via the link_to href — but the link itself uses an
    // in-page anchor (#section-<key>), so prefix the preview path here.
    event.preventDefault()
    window.location.assign(`/editor/native/preview#section-${this.keyValue}`)
  }

  // -----------------------------------------------------------------
  _refresh() {
    const plan = this._readPlan()
    const included  = this._isIncluded(plan)
    const proofread = !!(plan.meta?.proofread || {})[this.keyValue]

    // Include chip — eye-on/off + Included/Excluded label
    if (this.hasIncludeBtnTarget) {
      this.includeBtnTarget.classList.toggle("is-on",  included)
      this.includeBtnTarget.classList.toggle("is-off", !included)
      const icon = this.includeBtnTarget.querySelector("i, svg")
      if (icon) icon.setAttribute("data-lucide", included ? "eye" : "eye-off")
    }
    if (this.hasIncludeLabelTarget) {
      this.includeLabelTarget.textContent = included ? "Included" : "Excluded"
    }

    // Proofread chip — green pill when checked, gray otherwise
    if (this.hasProofreadCheckTarget) {
      this.proofreadCheckTarget.checked = proofread
      const label = this.proofreadCheckTarget.closest("label")
      label?.classList.toggle("is-on",  proofread)
      label?.classList.toggle("is-off", !proofread)
    }
    if (this.hasProofreadIconTarget) {
      this.proofreadIconTarget.setAttribute("data-lucide", proofread ? "check-circle" : "circle")
    }
    if (this.hasProofreadLabelTarget) {
      this.proofreadLabelTarget.textContent = proofread ? "Proofread" : "Not Proofread"
    }
    renderIcons(this.element)
  }

  _isIncluded(plan) {
    return isSectionIncluded(plan, this.keyValue)
  }

  _readPlan() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {} }
    catch (_) { return {} }
  }
  _writePlan(plan) {
    savePlan(plan)
    document.dispatchEvent(new CustomEvent("plan-store:change"))
  }
}
